import React, {useEffect, useRef, useCallback} from 'react';
import { motion } from "framer-motion";
import { useOnClickOutside, useLockedBody } from 'usehooks-ts';
import { useLocaleContext } from "../../../context/LocaleContext";
import CButton from '../CButton';
import CProtal from '../CPortal';
import { EVENT_GET_PARENT_SCROLL } from '../../../context/ModalContext';

import './style.scss';

/**
 * 
 * @param {object} children - Dom elements
 * @param {object} modalRef
 * @param {object} style - modal content inline styles
 * @param {boolean} isOpen 
 * @param {boolean} lockBody 
 * @param {boolean} hasBack 
 * @param {function} onBack 
 * @param {function} onClose 
 */
const CModal = ({
    children,
    index,
    isOpen,
    lockBody = true,
    hasBack = false,
    modalRef = null,
    style = {},
    onBack,
    onClose,
    appendTo,
}) => {
    const {get: getLocale} = useLocaleContext();
    const refModal = useRef(null);
    const refModalContent = useRef(null);
    const [_, setLocked] = useLockedBody(false, 'root');

    useOnClickOutside(refModal, handleClose);

    // useEffect(() => {
    //     if (refModalContent.current) {
    //         refModalContent.current.scrollTop = 0;
    //     }
    // }, [children]);

    useEffect(() => {
        if (refModalContent.current) {
            refModalContent.current.scrollTop = 0;
        }
    }, [index]);

    useEffect(() => {
        if (lockBody) setLocked(isOpen);
    }, [isOpen]);

    function handleClose() {
        if (lockBody) setLocked(false);
        onClose();
    }

    const setRefs = useCallback((element) => {
        refModal.current = element;
        if (modalRef) {
            modalRef.current = element;
        }

        if (!element) return;

        const resizeObserver = new ResizeObserver(() => {
            if (window.self !== window.top) {
                window.parent.postMessage({ event: EVENT_GET_PARENT_SCROLL }, '*');
            }
        });
        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, [modalRef]);

    return (
        <CProtal node={appendTo}>
            {
                isOpen
                    ? <motion.div
                        key="modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.1 }}
                        className="c-modal"
                        tabIndex={-1}
                    >
                        <div className="c-modal-backdrop" />
                        <div className="c-modal-wrapper">
                            <motion.div
                                ref={setRefs}
                                key="modalContent"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{ duration: 0.2, delay: isOpen ? 0.2 : 0 }}
                                className="c-modal-component"
                                style={style}
                            >
                                {
                                    hasBack &&
                                    <CButton
                                        blank
                                        label={getLocale("modal.back")}
                                        icon="chevron_left"
                                        size="xs"
                                        className="c-modal-back"
                                        onClick={() => onBack()}
                                    />
                                }
                                <CButton
                                    blank
                                    icon="close"
                                    className="c-modal-close"
                                    onClick={() => handleClose()}
                                />
                                <div className="c-modal-content container-scroll" ref={refModalContent}>
                                    {children}
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                    : <></>
            }
        </CProtal>
    )
};

export default CModal;
